import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import { Table, Calendar, Button, Popconfirm, Space, message, Tooltip } from "antd";
import Compact from "antd/es/space/Compact";
import { EditPrice, PrintedFormPrices } from "src/features/Modal";
import { DeleteOutlined } from "@ant-design/icons";
import { COL_PRICES } from "../../model/constants";
import { setColumnCellProps } from "../../lib/setCollumnCellProps";
import { PricesContext } from "src/shared/index";
import { EditableRow, PhotoCell } from "src/shared/index";
import { EditableCell } from "../lib/EditableCell";
import { apiRequests } from "src/shared/api/api";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(tz);

export default function Prices({
  handleRemove,
  handleSave,
  dataSource,
  dataSourceFilter,
  token,
  messageApi,
}) {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(12);
  const [countDataResponce, setCountDataResponce] = useState();

  const { categoriesData, priceTypesData, unitsData, websocket } =
    useContext(PricesContext);
  const [categories, setCategories] = useState(null);
  const [pirceType, setPriceType] = useState(null);
  const [units, setUnits] = useState(null);
  const [dataFromRequest, setDataFromRequest] = useState();
  const [tableloading, setTableLoading] = useState(false);
  // eslint-disable-next-line
  const [searchParams, setSearchParams] = useSearchParams();

  const handleSaveImage = useCallback(
    (picture) => {
      // PhotoCell сам обновляется через onRefresh из PhotoPopover
      // Не нужно обновлять локальное состояние, так как PhotoCell управляет своими данными
      if (!picture || !dataFromRequest) return;
    },
    [dataFromRequest]
  );

  const handleDeleteImage = useCallback(
    async (id) => {
      // PhotoCell сам обновляется через onRefresh из PhotoPopover
      // Не нужно обновлять локальное состояние, так как PhotoCell управляет своими данными
      if (!id || !dataFromRequest) return;
    },
    [dataFromRequest]
  );

  useEffect(() => {
    if (!websocket) return;

    const handlePicturesMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.target === "pictures" && data.result) {
          // PhotoCell сам обновляется через onRefresh из PhotoPopover
          // Просто обрабатываем сообщение без ошибок, но не обновляем локальное состояние
          if (data.action === "create") {
            handleSaveImage(data.result);
          }
          if (data.action === "delete" && data.result?.id) {
            handleDeleteImage(data.result.id);
          }
        }
      } catch (error) {
        console.error("Ошибка обработки websocket сообщения для pictures:", error);
      }
    };

    websocket.addEventListener('message', handlePicturesMessage);

    return () => {
      websocket.removeEventListener('message', handlePicturesMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, dataFromRequest, websocket?.readyState, handleSaveImage, handleDeleteImage]);

  useEffect(() => {
    if (unitsData && unitsData.length !== 0) {
      const unitsSelect = [];
      for (let item of unitsData) {
        unitsSelect.push({ value: item.id, text: item.name });
      }
      setUnits(unitsSelect);
    }
    if (priceTypesData && priceTypesData.length !== 0) {
      const priceTypeSelect = [];
      for (let item of priceTypesData) {
        priceTypeSelect.push({ value: item.id, text: item.name });
      }
      setPriceType(priceTypeSelect);
    }
    if (categoriesData && categoriesData.length !== 0) {
      const categoriesSelect = [];
      for (let item of categoriesData) {
        categoriesSelect.push({ value: item.id, text: item.name });
      }
      setCategories(categoriesSelect);
    }
  }, [categoriesData, priceTypesData, unitsData]);

  const editableCell = useCallback((cell) => (record, index) => ({
    record,
    index,
    editable: cell.editable,
    dataIndex: cell.dataIndex,
    handleEdit: handleSave,
  }), [handleSave]);

  const columns = useMemo(
    () =>
      setColumnCellProps(COL_PRICES, {
        image: [
          {
            key: "render",
            action: (_, record) => {
              // Для цен сначала показываем фото самой цены, если нет - фото номенклатуры
              const nomenclatureId = record.nomenclature || record.nomenclature_id;
              if (!nomenclatureId) {
                return null;
              }
              return (
                <PhotoCell
                  entity="prices"
                  entityId={record.id}
                  token={token}
                  size={45}
                  messageApi={messageApi}
                  fallbackEntity="nomenclature"
                  fallbackEntityId={nomenclatureId}
                />
              );
            },
          },
        ],
        nomenclature_name: [
          {
            key: "render",
            action: (record) => (
              <Tooltip placement="topLeft" title={record}>
                {record}
              </Tooltip>
            ),
          },
        ],
        category_name: [
          {
            key: "render",
            action: (record) => (
              <Tooltip placement="topLeft" title={record}>
                {record}
              </Tooltip>
            ),
          },
          {
            key: "filters",
            action: categories,
          },
          {
            key: "filterSearch",
            action: true,
          },
        ],
        unit_name: [
          {
            key: "filters",
            action: units,
          },
          {
            key: "filterSearch",
            action: true,
          },
        ],
    price: [
      {
        key: "onCell",
        action: editableCell,
      },
      {
        key: "sorter",
        action: (a, b) => a.price - b.price,
      },
    ],
    price_type: [
      {
        key: "filters",
        action: pirceType,
      },
    ],
    date_to: [
      {
        key: "onCell",
        action: editableCell,
      },
      {
        key: "render",
        action: (_, record) => {
          if (
            !record?.date_to ||
            record?.date_to === null ||
            record?.date_to === 0
          ) {
            return "не указана";
          } else {
            return dayjs
              .unix(record?.date_to)
              .tz("Europe/Moscow")
              .$d.toLocaleDateString();
          }
        },
      },
      {
        key: "filterDropdown",
        action: ({ selectedKeys, setSelectedKeys, confirm, clearFilters }) => (
          <div style={{ width: 300 }}>
            <Calendar
              fullscreen={false}
              headerRender={() => null}
              onChange={(e) => {
                setSelectedKeys([e]);
                confirm(true);
              }}
            />
            <Space
              style={{
                width: "100%",
                padding: "7px 8px",
                borderTop: "1px solid #f0f0f0",
                color: "rgba(0, 0, 0, 0.25)",
              }}
            >
              <Button
                type="link"
                size="small"
                disabled={selectedKeys.length === 0}
                onClick={() => {
                  clearFilters();
                  confirm({ closeDropdown: false });
                }}
              >
                Reset
              </Button>
            </Space>
          </div>
        ),
      },
    ],
    date_from: [
      {
        key: "onCell",
        action: editableCell,
      },
      {
        key: "render",
        action: (_, record) => {
          if (
            !record?.date_from ||
            record?.date_from === null ||
            record?.date_from === 0
          ) {
            return "не указана";
          } else {
            return dayjs
              .unix(record?.date_from)
              .tz("Europe/Moscow")
              .$d.toLocaleDateString();
          }
        },
      },
      {
        key: "filterDropdown",
        action: ({ selectedKeys, setSelectedKeys, confirm, clearFilters }) => (
          <div style={{ width: 300 }}>
            <Calendar
              fullscreen={false}
              headerRender={() => null}
              onChange={(e) => {
                setSelectedKeys([e]);
                confirm(true);
              }}
            />
            <Space
              style={{
                width: "100%",
                padding: "7px 8px",
                borderTop: "1px solid #f0f0f0",
                color: "rgba(0, 0, 0, 0.25)",
              }}
            >
              <Button
                type="link"
                size="small"
                disabled={selectedKeys.length === 0}
                onClick={() => {
                  clearFilters();
                  confirm({ closeDropdown: false });
                }}
              >
                Reset
              </Button>
            </Space>
          </div>
        ),
      },
    ],
        action: [
          {
            key: "render",
            style: { padding: "0" },
            action: (_, record) => (
              <Compact
                style={{
                  padding: "0",
                }}
              >
                <EditPrice record={record} handleSave={handleSave} />
                <PrintedFormPrices token={token} record={record} />
                <Tooltip title={"Удалить цену"}>
                  <Popconfirm
                    title={"Подтвердите удаление"}
                    onConfirm={() => {
                      handleRemove(record.id, dataFromRequest);
                    }}
                  >
                    <Button icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Tooltip>
              </Compact>
            ),
          },
          {
            key: "onCell",
            action: () => ({ style: { padding: 0 } }), // Убираем padding у ячеек колонки "Действие"
          },
        ],
      }),
    [
      dataFromRequest,
      handleRemove,
      handleSave,
      token,
      messageApi,
      categories,
      units,
      pirceType,
      editableCell,
    ]
  );

  const getData = async () => {
    setTableLoading(true);
    await apiRequests.prices
      .getItems({
        ...Object.fromEntries(searchParams.entries()),
        limit: perPage,
        page: page,
      })
      .then(({ data }) => {
        setDataFromRequest(data?.result);
        setCountDataResponce(data?.count);
      })
      .catch((err) => {
        let errorMessage = "Ошибка при получении данных";
        
        // Обработка Network Error
        if (err.code === 'ERR_NETWORK' || err.message === 'Network Error' || !err.response) {
          errorMessage = "Не удалось подключиться к серверу. Проверьте подключение к интернету.";
        } else if (err?.response?.data?.message) {
          errorMessage = typeof err.response.data.message === 'string' 
            ? err.response.data.message 
            : JSON.stringify(err.response.data.message);
        } else if (err?.response?.data?.detail) {
          errorMessage = typeof err.response.data.detail === 'string'
            ? err.response.data.detail
            : Array.isArray(err.response.data.detail)
            ? err.response.data.detail.map(e => typeof e === 'string' ? e : e?.msg || JSON.stringify(e)).join(', ')
            : JSON.stringify(err.response.data.detail);
        } else if (err?.message) {
          errorMessage = err.message;
        }
        
        if (messageApi) {
          messageApi.open({ type: 'error', content: errorMessage, duration: 4 });
        } else {
          message.error(errorMessage, 4);
        }
        setTableLoading(false);
      })
      .finally(() => {
        setTableLoading(false);
      });
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchParams]);

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    getData().finally(() => setPage(1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    // setDataFromRequest(dataSourceFilter?.result);
    // console.log(dataSourceFilter?.result);
    setCountDataResponce(dataSourceFilter?.count);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSourceFilter]);

  useEffect(() => {
    if (dataSource?.action === "create")
      setDataFromRequest((prev) => [dataSource?.result[0], ...prev]);
    if (dataSource?.action === "delete")
      setDataFromRequest((prev) =>
        prev.filter((item) => item.id !== dataSource.result.id)
      );
    if (dataSource?.action === "edit")
      setDataFromRequest((prev) =>
        prev.map((item) => {
          if (item.id !== dataSource.result.id) {
            return { ...item };
          } else {
            return { ...dataSource.result };
          }
        })
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource]);

  const handleChangePagination = async (current, pageSize) => {
    setPage(current);
    setPerPage(pageSize);
  };

  return (
    <>
      <Table
        pagination={{
          total: countDataResponce,
          onChange: handleChangePagination,
          pageSize: perPage,
          current: page,
        }}
        loading={tableloading}
        columns={columns}
        rowKey={(record) => record.id}
        rowClassName={() => "editable-row"}
        components={{
          body: {
            cell: EditableCell,
            row: EditableRow,
          },
        }}
        dataSource={dataFromRequest}
        bordered
        size="small"
        style={{ width: "100%" }}
      />
    </>
  );
}
